import { useFloating, autoUpdate, offset, flip, arrow, useInteractions, useHover, safePolygon, useFocus, useDismiss, useRole, useMergeRefs, FloatingPortal, FloatingArrow } from '@floating-ui/react';
import { useAnalytics } from '@magnetic/analytics';
import { withSubComponent } from '@magnetic/higher-order-components';
import { useClassNames } from '@magnetic/hooks';
import { mergeRefs } from '@magnetic/lib';
import React, { forwardRef, useRef, useCallback, useState, cloneElement } from 'react';
import { Divider } from './divider.js';
import './styles.css.js';

const ARROW_HEIGHT = 8;
const ARROW_WIDTH = 16;
const OFFSET_GAP = 4;
const Popover = withSubComponent(
  forwardRef(
    ({
      analytics,
      children = /* @__PURE__ */ React.createElement("div", null),
      className,
      disabled,
      kind = "default",
      open: controlledOpen,
      placement = "top",
      setOpen: controlledSetOpen,
      size = "md",
      style,
      title,
      ...props
    }, ref) => {
      const classNames = useClassNames(
        "mds-popover-rebuild",
        `mds-popover-rebuild-size-${size}`,
        `mds-popover-rebuild-size-${kind}`,
        className
      );
      const arrowRef = useRef(null);
      const { sendAnalytics } = useAnalytics();
      const handleMouseEnter = useCallback(() => {
        if (analytics == null ? void 0 : analytics.events.includes("mouseenter")) {
          sendAnalytics({
            component: "popover",
            ...analytics,
            event: "mouseenter"
          });
        }
      }, [analytics, sendAnalytics]);
      const [uncontrolledOpen, uncontrolledSetOpen] = useState(false);
      const open = controlledOpen != null ? controlledOpen : uncontrolledOpen;
      const setOpen = controlledSetOpen != null ? controlledSetOpen : uncontrolledSetOpen;
      const { context, refs, strategy, x, y } = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
          offset(ARROW_HEIGHT + OFFSET_GAP),
          flip({
            fallbackPlacements: ["top", "right", "bottom", "left"],
            padding: 6
          }),
          arrow({ element: arrowRef })
        ]
      });
      const enabled = !disabled && !controlledSetOpen;
      const { getFloatingProps, getReferenceProps } = useInteractions([
        useHover(context, {
          move: false,
          handleClose: safePolygon(),
          enabled
        }),
        useFocus(context, { enabled }),
        useDismiss(context),
        useRole(context, { role: "tooltip" }),
        { reference: { onMouseEnter: handleMouseEnter } }
      ]);
      const floatingRef = useMergeRefs([refs.setFloating]);
      if (kind === "chart") {
        return /* @__PURE__ */ React.createElement("div", {
          className: classNames
        }, title);
      }
      return /* @__PURE__ */ React.createElement(React.Fragment, null, cloneElement(children, {
        ref: mergeRefs([refs.setReference, children.props.ref, ref]),
        ...getReferenceProps()
      }), /* @__PURE__ */ React.createElement(FloatingPortal, null, open && !disabled && /* @__PURE__ */ React.createElement("div", {
        className: classNames,
        ref: floatingRef,
        style: {
          position: strategy,
          top: y != null ? y : 0,
          left: x != null ? x : 0,
          visibility: x === null ? "hidden" : "visible",
          ...style
        },
        ...props,
        ...getFloatingProps()
      }, /* @__PURE__ */ React.createElement(FloatingArrow, {
        className: "mds-popover-arrow",
        context,
        height: ARROW_HEIGHT,
        ref: arrowRef,
        width: ARROW_WIDTH
      }), title)));
    }
  ),
  { Divider }
);

export { Popover };
//# sourceMappingURL=index.js.map
